import React, { useRef, useState, useEffect } from "react";
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Box,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as d3 from "d3";
import NumberFormat from "react-number-format";
import { dateFormatter, dateFormatterV3, StyledTableRow } from "../../util/customFunctions";
import CopyTablesObjectv2 from "../../components/xlsx-export/CopyTablesObjectv2";
import StickyStyledTableHeader from "../../components/Containers/StickyStyledTableHeader";
import StickyStyledTableRow from "../../components/Containers/StickyStyledTableRow";

const useStyles = makeStyles({
  sticky: {
    position: "sticky",
    left: -1,
    background: "white",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      right: 0,
      width: "0.5px",
      background: "lightgrey",
    },
  },
  colHeader: {
    fontSize: "0.85rem",
    border: "0.5px solid lightgrey",
    padding: "5px 8px",
    color: "white",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "normal",
    wordWrap: "break-word",
    maxWidth: "120px",
  },
  dataCell: {
    fontSize: "0.85rem",
    border: "0.5px solid lightgrey",
    padding: "5px 8px",
    color: "inherit",
    fontWeight: "inherit",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  teamCell: {
    minWidth: "120px",
    maxWidth: "180px",
    whiteSpace: "normal",
    wordWrap: "break-word",
  },
  dateHeader: {
    maxWidth: "100px",
  },
  tableWrapper: {
    position: "relative",
  },
  copyButtonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "0.5rem",
    paddingRight: "8px",
  },
  selectControl: {
    minWidth: 120,
    "& .MuiSelect-root": {
      padding: "4px 8px",
      fontSize: "0.85rem",
    },
  },
  managerOverrideHeader: {
    backgroundColor: "#ff9800 !important",
  },
  managerTextField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: "28px",
      padding: 0,
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      fontSize: "0.85rem",
    },
    "& .MuiInputAdornment-root": {
      marginRight: "4px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  greenBackground: {
    backgroundColor: "#70ad47 !important",
    color: 'white'
  },
  orangeBackground: {
    backgroundColor: "#ed7c31 !important",
    color: 'white'
  },
  editableCell: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  acvOverrideCell: {
    minWidth: "130px",
    maxWidth: "130px",
    width: "130px",
  },
});

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: { name: props.name, value: values.value },
        });
      }}
      thousandSeparator
      isNumericString
      allowNegative={false}
    />
  );
}

const OpportunitiesTable = ({
  opportunities,
  columns,
  currencySymbol,
  valueLabel,
  onForecastCategoryChange,
  forecastCategories,
  isManager,
  onManagerOverrideChange,
}) => {
  const classes = useStyles();
  const tableRef = useRef();
  const tableContainerRef = useRef();
  const tableWrapperRef = useRef();

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isSticky, setIsSticky] = useState(false);
  const [rowPin, setrowPin] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [editingCell, setEditingCell] = useState(null); // { oppId, field }
  const [tempValue, setTempValue] = useState(""); // Store temporary value while editing

  const [tableWidth, setTableWidth] = useState("100%");



  const formatCellValue = (value, type) => {
    if (!value && value !== 0) return "-";

    switch (type) {
      case "currency":
        return `${currencySymbol}${d3.format(",")(Math.round(value))}`;
      case "date":
        return dateFormatterV3(value);
      default:
        return value;
    }
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortConfig.key === field && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: field, direction });
  };

  const validOpportunities = Array.isArray(opportunities) ? opportunities : [];

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return validOpportunities;

    return [...validOpportunities].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      aValue = aValue ?? "";
      bValue = bValue ?? "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (columns.find((col) => col.field === sortConfig.key)?.type === "date") {
        return sortConfig.direction === "asc"
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      return sortConfig.direction === "asc"
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
  }, [validOpportunities, sortConfig, columns]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;

    const handleScroll = () => {
      if (tableContainer) {
        if (tableContainer.scrollLeft > 0) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
        if (tableContainer.scrollTop > 0) {
          setrowPin(true);
        } else {
          setrowPin(false);
        }
      }
    };

    const calculateScrollbarWidth = () => {
      if (tableContainer) {
        const hasVerticalScrollbar = tableContainer.scrollHeight > tableContainer.clientHeight;
        if (hasVerticalScrollbar) {
          const scrollWidth = tableContainer.offsetWidth - tableContainer.clientWidth;
          setScrollbarWidth(scrollWidth);
        } else {
          setScrollbarWidth(0);
        }
      }
    };

    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
      calculateScrollbarWidth();
      
      const resizeObserver = new ResizeObserver(() => {
        calculateScrollbarWidth();
      });
      
      resizeObserver.observe(tableContainer);
      
      return () => {
        tableContainer.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [sortedData]);

  const categoriesList = forecastCategories || [];

  const isTeamColumn = (field) => {
    return field?.toLowerCase() === "team";
  };

  const isDateColumn = (type) => {
    return type === "date";
  };

  const handleDoubleClick = (oppId, field, currentValue) => {
    setEditingCell({ oppId, field });
    setTempValue(currentValue || ""); // Set temp value from current value
  };

  const handleBlur = (oppId, field) => {
    if (editingCell) {
      onManagerOverrideChange(oppId, field, tempValue); // Only update on blur
    }
    setEditingCell(null);
    setTempValue("");
  };

  const handleKeyDown = (e, oppId, field) => {
    if (e.key === "Enter") {
      handleBlur(oppId, field); // Update on Enter
    }
  };

  const isEditing = (oppId, field) => {
    return editingCell?.oppId === oppId && editingCell?.field === field;
  };

  // Get color class based on comparison
  const getACVColorClass = (overrideACV, originalACV) => {
    if (!overrideACV || overrideACV === "") return "";
    const override = parseFloat(overrideACV);
    const original = parseFloat(originalACV);
    
    if (override > original) return classes.greenBackground;
    if (override < original) return classes.orangeBackground;
    return "";
  };

  const getForecastCategoryColorClass = (overrideCategory, repCategory) => {
    if (!overrideCategory || overrideCategory === "-") return "";
    
    const overrideIndex = categoriesList.indexOf(overrideCategory);
    const repIndex = categoriesList.indexOf(repCategory);
    
    if (overrideIndex === -1 || repIndex === -1) return "";
    
    if (overrideIndex < repIndex) return classes.orangeBackground; // Higher in sequence
    if (overrideIndex > repIndex) return classes.greenBackground; // Lower in sequence
    return "";
  };

  // Get final values for Manager Rollup
  const getManagerRollupValues = (opp) => {
    const finalACV = opp.Manager_Override_ACV && opp.Manager_Override_ACV !== "" 
      ? parseFloat(opp.Manager_Override_ACV) 
      : parseFloat(opp.Manager_Rollup_ACV || opp.ACV || 0);
    
    const finalCategory = opp.Manager_Override_Forecast_Category && opp.Manager_Override_Forecast_Category !== "-"
      ? opp.Manager_Override_Forecast_Category
      : opp.Manager_Rollup_Forecast_Category || opp.Forecast_Category_Name || "-";
    
    return { finalACV, finalCategory };
  };

  useEffect(() => {
  if (tableContainerRef.current) {
    const width = tableContainerRef.current.offsetWidth - scrollbarWidth;
    setTableWidth(`${width}px`);
  }
}, [scrollbarWidth, sortedData]);

  return (
    <div style={{}}>
      {/* Table with Copy Button */}
      <div className={classes.tableWrapper} ref={tableWrapperRef}>
        {/* Title and Copy Button Container */}
        {/* Title - Centered Above */}
<Box
  style={{
    width: tableWidth,
    textAlign: "center",
    marginBottom: "4px",
  }}
>
  <Typography
    variant="h2"
    style={{
      fontWeight: 600,
      fontSize: "1.2rem",
    }}
  >
    Open Opportunities (Current Fiscal Quarter)
  </Typography>
</Box>

{/* Copy Button - Right Aligned */}
<Box
  style={{
    display: "flex",
    justifyContent: "flex-end",
    width: tableWidth,
    marginBottom: "0.5rem",
    // paddingRight: "8px",
    marginLeft: "1rem",
  }}
>
  <CopyTablesObjectv2 tableRef={tableRef} />
</Box>


        <TableContainer
          style={{
            borderLeft: isSticky ? "0.5px solid lightgrey" : "",
            //  borderTop: rowPin ? "0.5px solid lightgrey" : "",
            maxHeight: "81vh",
            width: "fit-content",
            maxWidth: "100%",
            border: '0.5px solid lightgrey'
          }}
          id="myTableContainer-opportunities"
          ref={tableContainerRef}
        >
          <Table ref={tableRef} data-test="opportunities-table" style={{ width: "auto" }}>
       

        <StickyStyledTableHeader>
  {/* First Header Row - STICKY - ONLY SHOW FOR MANAGERS */}
  {isManager && (
    <TableRow>
      <TableCell
        align="center"
        className={`${classes.colHeader}`}
        style={{
          // position: "sticky",
          // top: 0,
          // zIndex: 8,
          backgroundColor: "#5b9bd5",
          color: "white",
          cursor: "default",
          width: "50px",
          maxWidth: "50px",
          border: isSticky ? "0.5px solid lightgrey" : "",
        }}
      >
        
      </TableCell>
      
        <TableCell
          key={'idx'}
          align="center"
          className={`${classes.colHeader} ${
             classes.teamCell 
          } ${classes.dateHeader}`}
          colSpan={columns.length}
          style={{
            // position: "sticky",
            // top: 0,
            // zIndex: 8,
            backgroundColor: "#5b9bd5",
            color: "white",
            // maxWidth: isDateColumn(col.type) ? "100px" : "120px",
            border: isSticky ? "0.5px solid lightgrey" : "",
          }}
        >
          Rep Submission
        </TableCell>
      
      
      {/* Manager Override Columns */}
      <TableCell
        align="center"
        className={`${classes.colHeader}`}
        colSpan={2}
        style={{
          // position: "sticky",
          // top: 0,
          // zIndex: 8,
          background: '#bf8f00',
          color: 'white',
          border: isSticky ? "0.5px solid lightgrey" : "",
        }}
      >
        Manager Override
      </TableCell>
      <TableCell
        align="center"
        colSpan={2}
        className={`${classes.colHeader}`}
        style={{
          // position: "sticky",
          // top: 0,
          // zIndex: 8,
          background: '#70ad47',
          color: 'white',
          border: isSticky ? "0.5px solid lightgrey" : "",
        }}
      >
        Manager Forecast Roll Up
      </TableCell>
    </TableRow>
  )}
  
  {/* Second Header Row - STICKY */}
  <StickyStyledTableRow>
    <TableCell
      align="center"
      className={`${classes.colHeader}`}
      style={{
        // position: "sticky",
        // top: isManager ? "33px" : "0",
        // zIndex: 7,
        backgroundColor: "#4471c4",
        color: "white",
        cursor: "default",
        width: "50px",
        maxWidth: "50px",
      }}
    >
      #
    </TableCell>
    {columns.map((col, idx) => (
      <TableCell
        key={idx}
        align="center"
        onClick={() => handleSort(col.field)}
        className={`${classes.colHeader} ${
          isTeamColumn(col.field) ? classes.teamCell : ""
        } ${isDateColumn(col.type) ? classes.dateHeader : ""}`}
        style={{
          // position: "sticky",
          // top: isManager ? "33px" : "0",
          // zIndex: 7,
          backgroundColor:  "#4471c4" ,
          color: "white",
          maxWidth: isDateColumn(col.type) ? "100px" : "120px",
        }}
      >
        {col.label}
        <TableSortLabel
          active={sortConfig.key === col.field}
          direction={sortConfig.direction}
        />
      </TableCell>
    ))}
    
    {/* Manager Override Columns */}
    {isManager && (
      <>
        <TableCell
          align="center"
          className={`${classes.colHeader} ${classes.acvOverrideCell}`}
          style={{
            // position: "sticky",
            // top: "33px",
            // zIndex: 7,
            color: "white",
            backgroundColor:"#4471c4",
          }}
        >
          ACV 
        </TableCell>
        <TableCell
          align="center"
          className={`${classes.colHeader}`}
          style={{
            // position: "sticky",
            // top: "33px",
            // zIndex: 7,
            color: "white",
            backgroundColor: "#4471c4"
          }}
        >
          Forecast Category
        </TableCell>
        <TableCell
          align="center"
          className={`${classes.colHeader}`}
          style={{
            // position: "sticky",
            // top: "33px",
            // zIndex: 7,
            color: "white",
            minWidth: "100px",
            backgroundColor: "#4471c4",
          }}
        >
          ACV
        </TableCell>
        <TableCell
          align="center"
          className={`${classes.colHeader}`}
          style={{
            // position: "sticky",
            // top: "33px",
            // zIndex: 7,
            color: "white",
            minWidth: "120px",
            backgroundColor: "#4471c4",
          }}
        >
          Forecast Category
        </TableCell>
      </>
    )}
  </StickyStyledTableRow>
</StickyStyledTableHeader>
            <TableBody>
              {sortedData.length > 0 ? (
                sortedData.map((opp, index) => {
                  const { finalACV, finalCategory } = getManagerRollupValues(opp);
                  
                  return (
                    <StyledTableRow key={opp.Opp_ID || index}>
                      <TableCell
                        className={classes.dataCell}
                        align="center"
                        style={{ width: "50px" }}
                      >
                        {index + 1}
                      </TableCell>
                      {columns.map((col, colIdx) => (
                        <TableCell
                          key={colIdx}
                          className={`${classes.dataCell} ${
                            isTeamColumn(col.field) ? classes.teamCell : ""
                          }`}
                          align={
                            col.type === "string" || col.type === "dropDown"
                              ? "left"
                              : col.type === "currency" || col.type === "date" 
                              ? "right"
                              : "center"
                          }
                        >
                          {col.type === "dropDown" ? (
                            // Show as text for managers, dropdown for reps
                            isManager ? (
                              opp[col.field] || "-"
                            ) : (
                              <FormControl size="small" className={classes.selectControl}>
                                <Select
                                  disableUnderline
                                  value={opp[col.field] || col.options?.[0] || ""}
                                  onChange={(e) => {
                                    onForecastCategoryChange(
                                      opp.Opp_ID,
                                      e.target.value,
                                      e.target.value
                                    );
                                  }}
                                >
                                  {categoriesList.map((category) => (
                                    <MenuItem key={category} value={category}>
                                      {category}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )
                          ) : (
                            formatCellValue(opp[col.field], col.type)
                          )}
                        </TableCell>
                      ))}
                      
                      {/* Manager Override Cells */}
                      {isManager && (
                        <>
                          {/* ACV Override - Double click to edit with fixed width */}
                          <TableCell 
                            className={`${classes.dataCell} ${classes.acvOverrideCell} ${!isEditing(opp.Opp_ID, "Manager_Override_ACV") ? classes.editableCell : ""}`}
                            align="right"
                            onDoubleClick={() => handleDoubleClick(opp.Opp_ID, "Manager_Override_ACV", opp.Manager_Override_ACV)}
                          >
                            {isEditing(opp.Opp_ID, "Manager_Override_ACV") ? (
                              <TextField
                                type="text"
                                size="small"
                                variant="outlined"
                                autoFocus
                                className={classes.managerTextField}
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)} // Only update temp value
                                onBlur={() => handleBlur(opp.Opp_ID, "Manager_Override_ACV")}
                                onKeyDown={(e) => handleKeyDown(e, opp.Opp_ID, "Manager_Override_ACV")}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      {currencySymbol}
                                    </InputAdornment>
                                  ),
                                  inputComponent: NumberFormatCustom,
                                }}
                                placeholder="0"
                              />
                            ) : (
                              opp.Manager_Override_ACV 
                                ? `${currencySymbol}${d3.format(",")(Math.round(opp.Manager_Override_ACV))}`
                                : "-"
                            )}
                          </TableCell>
                          
                          {/* Forecast Category Override - NO COLOR */}
                          <TableCell 
                            className={classes.dataCell}
                            align="left"
                          >
                            <FormControl size="small" className={classes.selectControl}>
                              <Select
                                disableUnderline
                                value={opp.Manager_Override_Forecast_Category || "-"}
                                onChange={(e) =>
                                  onManagerOverrideChange(
                                    opp.Opp_ID,
                                    "Manager_Override_Forecast_Category",
                                    e.target.value
                                  )
                                }
                                displayEmpty
                              >
                                <MenuItem value="-">
                                  <em>-</em>
                                </MenuItem>
                                {categoriesList.map((category) => (
                                  <MenuItem key={category} value={category}>
                                    {category}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          
                          {/* Manager Rollup ACV - WITH COLOR */}
                          <TableCell 
                            className={`${classes.dataCell} ${getACVColorClass(opp.Manager_Override_ACV, opp.ACV)}`}
                            align="right"
                          >
                            {currencySymbol}{d3.format(",")(Math.round(finalACV))}
                          </TableCell>
                          
                          {/* Manager Rollup Forecast Category - WITH COLOR */}
                          <TableCell 
                            className={`${classes.dataCell} ${getForecastCategoryColorClass(
                              opp.Manager_Override_Forecast_Category, 
                              opp.Forecast_Category_Name 
                            )}`}
                            align="left"
                          >
                            {finalCategory}
                          </TableCell>
                        </>
                      )}
                    </StyledTableRow>
                  );
                })
              ) : (
                <StyledTableRow>
                  <TableCell
                    colSpan={columns.length + (isManager ? 5 : 1)}
                    align="center"
                    className={classes.dataCell}
                  >
                    No opportunities found
                  </TableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default OpportunitiesTable;